import React from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core';

import Accordion, { AccordionProps } from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { units } from '../theme/units';

const useStyles = makeStyles(theme => ({
  root: {
    zIndex: 1,
    '&$expanded': {
      zIndex: 1,
      marginTop: 0,
      backgroundColor: theme.palette.grey[50],
    },
  },
  expanded: {},
  content: {
    zIndex: 1,
  },
  withMargin: {
    margin: units.trbl(1.25, 0, 1.25, 0),
  },
}));

export interface Props {
  summary: React.ReactNode;
  details: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
  elevation?: AccordionProps['elevation'];
  square?: AccordionProps['square'];
  panelStyle?: React.CSSProperties;
  detailsClassname?: string;
  variant?: 'narrow' | 'default';
}

const Expandable: React.FC<Props> = ({
  summary,
  details,
  expanded,
  elevation,
  square,
  onChange,
  panelStyle,
  detailsClassname,
  variant = 'default',
  ...rest
}) => {
  const classes = useStyles();
  const handleChange = onChange
    ? // eslint-disable-next-line @typescript-eslint/ban-types
      (_: React.ChangeEvent<{}>, isExpanded: boolean) => onChange(isExpanded)
    : undefined;

  const contentClass = cx(classes.content, {
    [classes.withMargin]: variant !== 'narrow',
  });

  return (
    <Accordion
      style={panelStyle}
      classes={{
        expanded: classes.expanded,
        root: classes.root,
      }}
      expanded={expanded}
      elevation={elevation}
      square={square}
      onChange={handleChange}
      {...rest}
    >
      <AccordionSummary
        classes={{
          root: classes.root,
          expanded: classes.expanded,
          content: contentClass,
        }}
        expandIcon={<ExpandMoreIcon data-test-locator="Icon - ExpandMore" />}
      >
        {summary}
      </AccordionSummary>
      <AccordionDetails className={detailsClassname}>
        {details}
      </AccordionDetails>
    </Accordion>
  );
};

export { Expandable };
